import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyerTeamsApi,
  type AddBuyerTeamMemberRequest,
  type CreateBuyerTeamRequest,
  type UpdateBuyerTeamRequest,
} from "@/api/buyerTeams";
import { buyerAssignmentKeys, buyerTeamKeys } from "./buyerKeys";

/**
 * Buyer teams (WS4) — the registry, one team's detail, and the roster moves.
 *
 * 🔴 <b>EVERY mutation here invalidates `buyerTeamKeys.all`, including the
 * ones that look local.</b> A membership add is a MOVE whenever the person was
 * already on another team (D1), so it changes the roster on TWO teams and the
 * member counts on the registry list — and the response carries only the team
 * they landed on. A narrower key would leave the team somebody was moved OFF
 * still listing them for the five minutes of `staleTime`, with
 * `refetchOnWindowFocus: false` and therefore no recovery. The one write whose
 * blast radius is genuinely one team (a rename) uses the same prefix rather
 * than a second rule nobody would remember to apply.
 */

/** The registry. `includeArchived` is part of the key, so the toggle refetches
 *  rather than serving the previous answer. */
export function useBuyerTeams(includeArchived = false) {
  return useQuery({
    queryKey: buyerTeamKeys.list(includeArchived),
    queryFn: () => buyerTeamsApi.list(includeArchived),
  });
}

/** One team, with its roster and lead rollups. */
export function useBuyerTeam(id: string | undefined) {
  return useQuery({
    queryKey: buyerTeamKeys.detail(id ?? ""),
    queryFn: () => buyerTeamsApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useCreateBuyerTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBuyerTeamRequest) => buyerTeamsApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerTeamKeys.all });
    },
  });
}

export function useUpdateBuyerTeam(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBuyerTeamRequest) => buyerTeamsApi.update(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerTeamKeys.all });
    },
  });
}

/** Archive / unarchive, as one mutation because the confirm and the toast are
 *  the only things that differ and both are the caller's anyway. */
export function useSetBuyerTeamArchived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived ? buyerTeamsApi.archive(id) : buyerTeamsApi.unarchive(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerTeamKeys.all });
    },
  });
}

/**
 * Add a staff user to this team — or MOVE them here from another one.
 *
 * <p>Also invalidates the assignment-queue prefix: which specialists a team's
 * queue may hand a lead to is derived from that team's roster, so a move that
 * left the queue's cached answer alone would offer a specialist who is no
 * longer on the team, or withhold one who now is.</p>
 */
export function useAddBuyerTeamMember(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddBuyerTeamMemberRequest) => buyerTeamsApi.addMember(teamId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerTeamKeys.all });
      void qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
    },
  });
}

/** Remove somebody from this team. The buyers they hold are untouched — this
 *  is a roster change, not a reassignment — but the queue's eligible-specialist
 *  list is roster-derived, so it is invalidated alongside. */
export function useRemoveBuyerTeamMember(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => buyerTeamsApi.removeMember(teamId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerTeamKeys.all });
      void qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
    },
  });
}
